import { Button } from '@/components/custom/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/custom/label'
import { useEffect, useRef } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createGroup, updateGroup } from '@/api/settings'
import { toast } from 'sonner'
import { throwException } from '@/exceptions/throw-exception.js'
import QUERY_KEY from '@/constants/use-query-key.js'

const formSchema = z
  .object({
    name: z
      .string()
      .nonempty({ message: 'Tên nhóm khách hàng không được bỏ trống' }),
  })
  .superRefine((data, ctx) => {
    if (!data.name.trim()) {
      ctx.addIssue({
        path: ['name'],
        message: 'Tên nhóm khách hàng không được bỏ trống',
      })
    }
  })

export default function DialogCustomerGroup({
  open,
  customerGroup,
  handleClose,
}) {
  const queryClient = useQueryClient()
  const formRef = useRef(null)
  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
    },
  })

  const createGroupMutation = useMutation({
    mutationFn: createGroup,
    onSuccess: () => {
      toast.success('Tạo mới nhóm khách hàng thành công')

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_LIST_CUSTOMER_GROUPS],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_ALL_CUSTOMER_GROUPS],
      })

      handleClose()
    },
    onError: throwException,
  })

  const editGroupMutation = useMutation({
    mutationFn: updateGroup,
    onSuccess: () => {
      toast.success('Cập nhật nhóm khách hàng thành công')

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_LIST_CUSTOMER_GROUPS],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_ALL_CUSTOMER_GROUPS],
      })

      handleClose()
    },
    onError: throwException,
  })

  const handleCreateCustomerGroup = (payload) => {
    createGroupMutation.mutate({ data: payload })
  }

  const handleEditCustomerGroup = (customerGroupId, payload) => {
    editGroupMutation.mutate({
      id: customerGroupId,
      data: payload,
    })
  }

  const onSubmit = (fieldsValue) => {
    if (!customerGroup) {
      handleCreateCustomerGroup({
        name: fieldsValue.name.trim(),
        target: 'end_user',
      })
    } else {
      handleEditCustomerGroup(customerGroup.id, {
        id: customerGroup.id,
        name: fieldsValue.name.trim(),
        target: 'end_user',
      })
    }
  }

  useEffect(() => {
    if (customerGroup) {
      const fieldsValue = {
        name: customerGroup.name,
      }

      form.reset(fieldsValue)
    }
  }, [customerGroup, form])

  return (
    <Dialog open={open} onOpenChange={() => handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {!customerGroup
              ? 'Thêm mới nhóm khách hàng'
              : 'Cập nhật nhóm khách hàng'}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription
          className="hidden"
          aria-describedby="groups-dialog"
        />
        <Form {...form}>
          <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <Label isRequired>Tên nhóm khách hàng</Label>
                    <FormControl>
                      <Input {...field} placeholder="Tên nhóm khách hàng" />
                    </FormControl>
                    {form.formState.errors && form.formState.errors.name && (
                      <span className="text-sm text-[--label-red-foreground]">
                        {form.formState.errors.name.message}
                      </span>
                    )}
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
        <DialogFooter className="sm:justify-right">
          <DialogClose asChild>
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleClose()}
            >
              Đóng
            </Button>
          </DialogClose>
          <Button type="submit" onClick={() => formRef.current.requestSubmit()}>
            {!customerGroup ? 'Thêm mới' : 'Cập nhật'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
